import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, IconButton,
  Snackbar, Alert, Divider, InputAdornment, Chip,
} from '@mui/material';
import db from '../../data/demoDatabase';
import StatusBadge from '../../components/common/StatusBadge';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import TelegramIcon from '@mui/icons-material/Telegram';
import XIcon from '@mui/icons-material/X';
import EmailIcon from '@mui/icons-material/Email';
import ShareIcon from '@mui/icons-material/Share';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import StarIcon from '@mui/icons-material/Star';
import SendIcon from '@mui/icons-material/Send';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useAuth } from '../../context/AuthContext';
import StatCard from '../../components/common/StatCard';
import DataTable from '../../components/common/DataTable';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const howItWorks = [
  { icon: ShareIcon, title: 'Share Your Link', description: 'Send your unique referral link to friends and family via social media or messaging apps.' },
  { icon: PersonAddIcon, title: 'Friend Registers', description: 'Your friend signs up on iMoney using your referral link and creates an account.' },
  { icon: GroupIcon, title: 'Becomes Your Downline', description: 'Your friend is automatically added as your downline in the referral network.' },
  { icon: StarIcon, title: 'Both Earn Tokens', description: 'You and your friend both receive bonus tokens when they apply for financial products.' },
];

const downlineColumns = [
  {
    field: 'name', headerName: 'Name',
    renderCell: (row) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name}</Typography>
        <Typography variant="caption" color="text.secondary">{row.email}</Typography>
      </Box>
    ),
  },
  {
    field: 'status', headerName: 'Status', width: 120,
    renderCell: (row) => <StatusBadge status={row.status === 'active' ? 'Active' : 'Pending'} />,
  },
  { field: 'productApplied', headerName: 'Latest Product Applied', flex: 1.2 },
  {
    field: 'tokensEarned', headerName: 'Tokens Earned', width: 140, align: 'center',
    renderCell: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#7B1FA2' }}>
        {row.tokensEarned}
      </Typography>
    ),
  },
  { field: 'joinedDate', headerName: 'Joined Date', width: 130 },
];

export default function InviteFriends() {
  const { user } = useAuth();
  const referralLink = `https://www.imoney.my/ref/${user?.referralCode || 'DEMO123'}`;

  const [refreshKey, setRefreshKey] = useState(0);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [inviteSnackbar, setInviteSnackbar] = useState(false);
  const [contactValue, setContactValue] = useState('');

  // Read downline from db
  const downlineData = useMemo(() => {
    if (!user?.id) return [];
    // Users who have referredBy = current user, OR whose cases came through current user's referral code
    const allUsers = db.users.getAll();
    const allCases = db.cases.getAll();
    const direct = allUsers.filter((u) => u.referredBy === user.id && u.id !== user.id);
    return direct.map((u) => {
      const theirCases = allCases.filter(
        (c) => c.applicantId === u.id || c.referralOwnerId === u.id
      );
      const latestCase = theirCases.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )[0];
      // Tokens earned by referrer for this downline's cases
      const tokensEarned = theirCases
        .filter((c) => ['Approved', 'Disbursed'].includes(c.status))
        .reduce((sum) => sum + 20, 0); // rough estimate; real tokens are in notifications
      return {
        id: u.id,
        name: u.name,
        email: u.email,
        status: u.status,
        productApplied: latestCase
          ? `${latestCase.productName} - ${latestCase.bank}`
          : '—',
        tokensEarned,
        joinedDate: u.joinDate,
      };
    });
  }, [user, refreshKey]);

  // Read invites created by this user (if any)
  const inviteStats = useMemo(() => {
    if (!user?.id) return { sent: 0, accepted: 0, pending: 0, tokens: 0 };
    const allInvites = db.invites.getAll().filter((i) => i.createdBy === user.id);
    const accepted = allInvites.filter((i) => i.status === 'approved').length;
    const pendingStatuses = ['sent', 'opened', 'submitted'];
    const pending = allInvites.filter((i) => pendingStatuses.includes(i.status)).length;
    // Tokens earned from downline — sum of downline tokens
    const tokens = downlineData.reduce((sum, d) => sum + d.tokensEarned, 0);
    return {
      sent: allInvites.length,
      accepted,
      pending,
      tokens,
    };
  }, [user, downlineData, refreshKey]);

  // Check if social_share task has already been claimed
  const socialShareClaimed = useMemo(() => {
    if (!user?.id) return true;
    return db.tokenTransactions.getByUserId(user.id)
      .some((t) => t.source === 'task:social_share');
  }, [user, refreshKey]);

  // Fire the one-time social_share task bonus on first share action
  const [taskBonusSnack, setTaskBonusSnack] = useState({ open: false, msg: '' });
  const triggerShareTask = () => {
    if (!user?.id || socialShareClaimed) return;
    const result = db.engine.awardTaskBonus(user.id, 'social_share', user);
    if (result?.amount && !result.alreadyEarned) {
      setTaskBonusSnack({
        open: true,
        msg: `🎉 +${result.amount} tokens earned for sharing!${result.escrowed ? ' (pending KYC)' : ''}`,
      });
      setRefreshKey((k) => k + 1);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopySnackbar(true);
      triggerShareTask();
    }).catch(() => {
      setCopySnackbar(true);
      triggerShareTask();
    });
  };

  const handleSocialClick = (url) => {
    window.open(url, '_blank');
    triggerShareTask();
  };

  const handleSendInvite = () => {
    if (!contactValue.trim()) return;
    // Create a real invite record (engine handles audit log)
    db.engine.createInvite(
      {
        role: 'free_user',
        recipientEmail: contactValue.trim(),
        expiryDays: 30,
        note: `Invited by ${user?.name} via Invite Friends page`,
      },
      user
    );
    setInviteSnackbar(true);
    setContactValue('');
    setRefreshKey((k) => k + 1);
  };

  const shareMessage = encodeURIComponent(`Join iMoney and earn rewards! Sign up here: ${referralLink}`);
  const shareSubject = encodeURIComponent('Join iMoney');
  const shareBody = encodeURIComponent(`Hey! I've been using iMoney to compare and apply for financial products. Join using my link and we both earn rewards: ${referralLink}`);

  const socialButtons = [
    { icon: <WhatsAppIcon />, color: '#25D366', label: 'WhatsApp', url: `https://wa.me/?text=${shareMessage}` },
    { icon: <FacebookIcon />, color: '#1877F2', label: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}` },
    { icon: <TelegramIcon />, color: '#0088CC', label: 'Telegram', url: `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${shareMessage}` },
    { icon: <XIcon />, color: '#000000', label: 'X / Twitter', url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(referralLink)}&text=${shareMessage}` },
    { icon: <EmailIcon />, color: '#757575', label: 'Email', url: `mailto:?subject=${shareSubject}&body=${shareBody}` },
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}>
        Invite Friends
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Share your referral link and earn tokens when friends join iMoney
      </Typography>

      {/* Referral Link Section */}
      <Card sx={{
        mb: 4, borderRadius: 3,
        background: `linear-gradient(135deg, ${PRIMARY} 0%, #2A4A7F 100%)`,
        color: '#fff',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Your Referral Link
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 3 }}>
                <TextField
                  fullWidth
                  value={referralLink}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      color: '#fff',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    },
                  }}
                  size="small"
                />
                <Button
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyLink}
                  sx={{
                    bgcolor: SECONDARY, color: '#fff', fontWeight: 600,
                    borderRadius: 2, px: 3, whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#00A381' },
                  }}
                >
                  Copy Link
                </Button>
              </Box>

              {/* Social Share Buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Share via
                </Typography>
                {!socialShareClaimed && (
                  <Chip
                    label="+5 tokens on first share"
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255,215,0,0.25)',
                      color: '#FFD700',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 20,
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {socialButtons.map((btn) => (
                  <IconButton
                    key={btn.label}
                    onClick={() => handleSocialClick(btn.url)}
                    title={btn.label}
                    sx={{
                      bgcolor: btn.color, color: '#fff',
                      width: 48, height: 48,
                      '&:hover': { bgcolor: btn.color, opacity: 0.85 },
                    }}
                  >
                    {btn.icon}
                  </IconButton>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{
                width: 150, height: 150, borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.15)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                border: '2px dashed rgba(255,255,255,0.4)',
              }}>
                <QrCode2Icon sx={{ fontSize: 64, opacity: 0.8, mb: 1 }} />
                <Typography variant="caption" sx={{ opacity: 0.7 }}>QR Code</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invite by Contact */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY, mb: 2 }}>
            Invite by Contact
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              placeholder="Enter phone number or email address"
              value={contactValue}
              onChange={(e) => setContactValue(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonAddIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSendInvite}
              disabled={!contactValue.trim()}
              sx={{
                bgcolor: PRIMARY, fontWeight: 600, borderRadius: 2, px: 3, whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#142A4A' },
              }}
            >
              Send Invite
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY, mb: 3 }}>
        How It Works
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {howItWorks.map((step, index) => {
          const StepIcon = step.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{
                height: '100%', borderRadius: 3, textAlign: 'center',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
                    bgcolor: `${SECONDARY}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <StepIcon sx={{ fontSize: 32, color: SECONDARY }} />
                  </Box>
                  <Typography variant="caption" sx={{
                    fontWeight: 700, color: SECONDARY,
                    bgcolor: `${SECONDARY}15`, px: 1.5, py: 0.5, borderRadius: 10,
                  }}>
                    Step {index + 1}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: PRIMARY, mt: 1.5, mb: 1 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ mb: 4 }} />

      {/* Referral Stats */}
      <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY, mb: 3 }}>
        Referral Stats
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Invites Sent" value={inviteStats.sent} icon={PeopleIcon} color={PRIMARY} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Accepted" value={inviteStats.accepted} icon={CheckCircleIcon} color={SECONDARY} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Pending" value={inviteStats.pending} icon={HourglassEmptyIcon} color="#F57F17" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Downline Tokens" value={inviteStats.tokens} icon={EmojiEventsIcon} color="#7B1FA2" />
        </Grid>
      </Grid>

      {/* My Downline */}
      <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY, mb: 3 }}>
        My Downline ({downlineData.length})
      </Typography>
      {downlineData.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No downline yet. Share your referral link — when a friend registers with your code, they'll appear here.
        </Alert>
      ) : (
        <DataTable
          columns={downlineColumns}
          rows={downlineData}
          title="Referred Users"
          searchable
          pageSize={5}
        />
      )}

      {/* Snackbars */}
      <Snackbar open={copySnackbar} autoHideDuration={3000} onClose={() => setCopySnackbar(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setCopySnackbar(false)} severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          Referral link copied to clipboard!
        </Alert>
      </Snackbar>
      <Snackbar open={inviteSnackbar} autoHideDuration={3000} onClose={() => setInviteSnackbar(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setInviteSnackbar(false)} severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          Invite sent successfully!
        </Alert>
      </Snackbar>
      <Snackbar
        open={taskBonusSnack.open}
        autoHideDuration={4000}
        onClose={() => setTaskBonusSnack({ open: false, msg: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setTaskBonusSnack({ open: false, msg: '' })}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          {taskBonusSnack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
